/**
 * ChartOfAccountsTab — searchable, type-filterable list of synced Xero accounts
 * with per-account dropdown-visibility toggles. Expands the account-visibility
 * list that used to live in Settings → Organization with search + type filter.
 */
import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

const ALL_TYPES = '__all__';

interface ChartOfAccountsTabProps {
  /** Visibility is an adminProcedure on the backend — non-admins view read-only. */
  isAdmin: boolean;
}

export function ChartOfAccountsTab({ isAdmin }: ChartOfAccountsTabProps) {
  const utils = trpc.useUtils();
  const accountsQuery = trpc.xero.getAccounts.useQuery(
    { dropdownOnly: false },
    { staleTime: 30_000 }
  );

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES);

  const visibilityMutation = trpc.xero.updateAccountVisibility.useMutation({
    onSuccess: async () => {
      await utils.xero.getAccounts.invalidate();
    },
    onError: (error) => toast.error(error.message || 'Failed to update account visibility'),
  });

  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);

  const types = useMemo(() => {
    const set = new Set<string>();
    for (const account of accounts) {
      if (account.account_type) set.add(account.account_type);
    }
    return Array.from(set).sort();
  }, [accounts]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return accounts.filter((account) => {
      if (typeFilter !== ALL_TYPES && account.account_type !== typeFilter) return false;
      if (!term) return true;
      return (
        account.account_code.toLowerCase().includes(term) ||
        account.name.toLowerCase().includes(term)
      );
    });
  }, [accounts, search, typeFilter]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Account visibility</CardTitle>
        <CardDescription>
          Choose which accounts appear in dropdowns across purchase orders and shipments.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-xs">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              placeholder="Search code or name…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-8 pl-9 text-sm"
              aria-label="Search accounts"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 w-full text-sm sm:w-[200px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TYPES}>All types</SelectItem>
              {types.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {accountsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading chart of accounts…</p>
        ) : accounts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No accounts yet. Run a chart-of-accounts sync on the Sync tab after connecting Xero.
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No accounts match "{search.trim()}".
          </p>
        ) : (
          <div className="divide-y rounded-md border">
            {filtered.map((account) => (
              <div
                key={account.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {account.account_code} · {account.name}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {account.account_type || '-'}
                  </p>
                </div>
                <Switch
                  checked={Boolean(account.show_in_dropdown)}
                  disabled={visibilityMutation.isPending || !isAdmin}
                  onCheckedChange={(checked) =>
                    visibilityMutation.mutate({ accountId: account.id, showInDropdown: checked })
                  }
                  aria-label={`Show ${account.account_code} in dropdowns`}
                  title={isAdmin ? undefined : 'Account visibility changes require Admin access'}
                />
              </div>
            ))}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground">
          Showing {filtered.length} of {accounts.length} accounts.
        </p>
      </CardContent>
    </Card>
  );
}
